import { Fragment, useState, useRef, useMemo } from 'react';
import { graphql } from 'gatsby';
import SecondaryNavigationMenu from './components/SecondaryNavigationMenu/SecondaryNavigationMenu';
import MenuItem from '../Header/components/MenuItem/MenuItem';
import { MenuContext } from '../Header/contexts/MenuContext';
import {
  StyledSticky,
  StyledCTA,
  CTASectionDesktop,
  CTASectionMobile,
  SecondaryMenu,
  SecondaryMenuItem,
  StyledContainer,
  StyledSecondaryMenuItem,
  StyledSection,
  StyledOverlay,
} from './SecondaryNavigation.styles';
import { useLocation } from '@reach/router';
import { type BlockHTMLId } from '@/utils/renderBlock';
import useMenuClickOutside from '@/hooks/useMenuClickOutside';

export interface SecondaryNavigationProps extends Queries.SECONDARY_NAVIGATIONFragment {
  pageContext: PageContext;
}

const SecondaryNavigation = ({
  secondaryNavigationElements,
  pageContext,
  id,
}: SecondaryNavigationProps & BlockHTMLId) => {
  const location = useLocation();

  const shouldShowCTAsOnScrollOnly =
    secondaryNavigationElements.options?.value?.includes('show_ctas_on_scroll') ?? false;
  const root = secondaryNavigationElements.root?.value ?? [];
  const ctas = secondaryNavigationElements.ctas?.value ?? [];
  const [openMenuItemId, setOpenMenuItemId] = useState<string | null>(null);
  const menuButtonsRef = useRef<Set<HTMLElement>>(new Set());
  const dropdownContentsRef = useRef<Set<HTMLElement>>(new Set());

  const isMenuItemActive = (item: Queries.HEADER_MENU_ITEMFragment) => {
    const columns = item.elements.columns.value ?? [];
    const sections = columns.flatMap((column) => column.elements.sections.value ?? []);
    const links = sections.flatMap((section) => section.elements.links.value ?? []);
    return links.some((link) => isLinkActive(link.elements.link.value));
  };

  const isLinkActive = (link: Queries.LINKFragment) => {
    const action = link.linkElements.action.value;
    if (action.__typename === 'kontent_item_link_action_external_page') {
      const url = new URL(action.elements.url.value);
      const linkFullPath = url.pathname + url.search + url.hash;
      const currentFullPath = location.pathname + location.search + location.hash;
      return currentFullPath === linkFullPath;
    } else if (action.__typename === 'kontent_item_link_action_internal_page') {
      return pageContext.pageCodename === action.elements.page.value.system.codename;
    }

    return false;
  };

  const clearActiveMenuItem = () => {
    setOpenMenuItemId(null);
  };

  const menuContextValue = useMemo(
    () => ({
      activeMenuItemId: openMenuItemId,
      setActiveMenuItemId: setOpenMenuItemId,
      menuButtonsRef,
      dropdownContentsRef,
    }),
    [openMenuItemId, setOpenMenuItemId, menuButtonsRef, dropdownContentsRef]
  );

  // Handle click-outside behavior for menu dropdowns
  useMenuClickOutside(openMenuItemId, setOpenMenuItemId, menuButtonsRef, dropdownContentsRef);

  const getCTAs = (hasLineBreak = false) => {
    return ctas.map((cta, index) => {
      return (
        <Fragment key={cta.system.id}>
          <StyledCTA {...cta} variant={index === 0 ? 'primary_small' : 'secondary_small'} />
          {hasLineBreak && <br />}
        </Fragment>
      );
    });
  };

  return (
    <>
      <StyledSticky showCTAsOnScroll={shouldShowCTAsOnScrollOnly}>
        <StyledSection data-block-secondary-navigation id={id}>
          <StyledContainer>
            <SecondaryNavigationMenu>
              <MenuContext.Provider value={menuContextValue}>
                <SecondaryMenu role="menu">
                  {root.map((item) => (
                    <SecondaryMenuItem key={item.system.id}>
                      {item.__typename === 'kontent_item_header_menu_item' ? (
                        <MenuItem
                          {...item}
                          isHighlighted={isMenuItemActive(item)}
                          onLinkClick={clearActiveMenuItem}
                        />
                      ) : (
                        <StyledSecondaryMenuItem
                          variant="nav_text_link"
                          {...item}
                          isActive={isLinkActive(item)}
                        ></StyledSecondaryMenuItem>
                      )}
                    </SecondaryMenuItem>
                  ))}
                </SecondaryMenu>
              </MenuContext.Provider>
              <CTASectionMobile>{getCTAs(true)}</CTASectionMobile>
            </SecondaryNavigationMenu>
            <CTASectionDesktop>{getCTAs()}</CTASectionDesktop>
          </StyledContainer>
        </StyledSection>
      </StyledSticky>
      <StyledOverlay
        data-testid="overlay"
        isMenuActive={openMenuItemId !== null}
        onClick={() => {
          setOpenMenuItemId(null);
        }}
      />
    </>
  );
};

export const fragment = graphql`
  fragment SECONDARY_NAVIGATION on kontent_item_secondary_navigation {
    secondaryNavigationElements: value {
      title {
        value
      }
      root {
        value {
          __typename
          ... on kontent_item {
            system {
              id
            }
          }
          ...HEADER_MENU_ITEM
          ...LINK
        }
      }
      ctas {
        value {
          system {
            id
          }
          ...LINK
        }
      }
      options {
        value
      }
    }
  }
`;

export default SecondaryNavigation;
